import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { BasicButtonColors, BasicButtonModes, BasicButtonSizes } from '@app/shared/enums/buttons';
import { UploadFileService } from '@app/shared/services/utils/upload-files.service';
import { UtilService } from '@app/shared/services/utils/util.service';
import { TranslateService } from '@ngx-translate/core';
import { Operation } from 'fast-json-patch';
import { DrawerService } from '../drawer/drawer.service';
import {
  docActionMenuItems,
  fileEntityTypes,
  fileExtensions,
  FileTypeEnum,
  FileUploadStatusEnum,
  IFile,
} from './constants';
import { FileUploadService } from '@app/shared/services/utils/file-upload.service';
import { ImageService } from '@app/shared/services/images/image.service';
import { SnackBarService } from '../snackbar/snackbar.service';
import { SnackBarsTypes } from '@app/shared/enums/snackbars';

@Component({
  selector: 'cm-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
})
export class FileUploadComponent implements OnInit {
  @ViewChild('clickDocActionMenu') clickDocActionMenu: MatMenuTrigger;
  docActionMenuItems = docActionMenuItems;
  @ViewChild('fileInput') fileInput: any;
  @Output() fileChanged = new EventEmitter<any>();
  @Output() patchDocumentName = new EventEmitter<any>();
  @Output() removeFile = new EventEmitter<any>();
  @Output() removePlaceholder = new EventEmitter<any>();
  @Output() fileLoadingFlag = new EventEmitter<boolean>();
  @Output() downloadErrors = new EventEmitter<any>();
  @Output() urlUpdated = new EventEmitter<any>();
  @Input() type: string;
  @Input() view: string;
  @Input() buttonLabel: string;
  @Input() hideInputWhileUpload = false;
  @Input() fileSizeLimitMB;
  @Input() uploadFlow: boolean;
  @Input() entityName: string;
  @Input() allowMultiple: boolean;
  @Input() fileType: FileTypeEnum;
  @Input() removeAfterUpload: boolean;
  @Input() fileUploadFailed: boolean;
  @Input() fileHasParsingErrors: boolean;
  @Input() dataCy: string;
  @Input() hideMoreActions: boolean;
  @Input() hideFileUpload: boolean;
  @Input() cropImage: boolean;
  @Input() displayPlaceholder!: {
    authUrl: string;
    fallbackUrl: string;
    name?: string };

  BasicButtonModes = BasicButtonModes;
  BasicButtonColors = BasicButtonColors;
  modifiedFile: IFile;
  public extensions = fileExtensions;
  BasicButtonSizes = BasicButtonSizes;
  files: IFile[];
  fileIndex: number;
  public spinnerDiameter = 40;
  kbSize = 1024;
  dotCharacter = '&#8226;';
  mbSize = 1048576;

  constructor(
    public fileUploadService: FileUploadService,
    public uploadFileService: UploadFileService,
    public snackBarService: SnackBarService,
    private _drawerService: DrawerService,
    private _imageService: ImageService,
    private _ts: TranslateService
  ) {
    const userAgent = navigator.userAgent;
    if (userAgent.indexOf('Firefox') != -1) {
      this.spinnerDiameter = 20;
    }
  }

  ngOnInit(): void {
    // Reset cached items in service
    this.fileUploadService.setFiles([]);
    this.generateContent();
  }

  generateContent(): void {
    this.extensions[this.type].label = this._ts.instant(this.extensions[this.type].label);
  }

  get fileList() {
    return this.fileUploadService.getFiles(this.entityName, this.fileType) || [];
  }

  async fileEvent(event) {
    this.fileUploadService.reset(this.entityName);
    this.cropImage = event?.cropImage !== undefined ? event?.cropImage : this.cropImage;
    this.removeAfterUpload = event?.removeAfterUpload !== undefined ? event?.removeAfterUpload : this.removeAfterUpload;

    this.fileLoadingFlag.emit(true);
    const file = (event?.target?.files || [])[0];
    const fileName = this.cleanFileName(file?.name);
    const modifiedFile = new File([file], fileName, { type: file.type });
    const ext = this.extensions[this.type]?.accept || [];
    const validExt = ext.some((e) => e == modifiedFile?.type);

    /*
      This is necessary because the fileEvent function will run before the native
      file type validation since it has the async modifier
    */
    if (!validExt) return;

    const sizeInMB = Number((file?.size / this.mbSize).toFixed(2));
    const sizeError = sizeInMB > this.fileSizeLimitMB;

    if (this.cropImage) {
      this.fileChanged.emit(event);
      return;
    }

    if (!sizeError) {
      try {
        if (this.uploadFlow) {
          this.fileIndex = this.setTempFile(modifiedFile);
          await UtilService.timeOut(2000);

          this.fileUploadService.uploadFinished(this.fileIndex);

          //we check in case the file was deleted
          if (this.fileList.length > 0) this.fileChanged.emit(modifiedFile);
        } else {
          this.fileIndex = this.setTempFile(modifiedFile);
          this.uploadFileService.initUploadFile(modifiedFile).subscribe((response) => {
            this.fileLoadingFlag.emit(false);
            const { uri } = response;
            this.sendUploadedFile(modifiedFile, sizeInMB, uri, this.fileIndex);
            if (this.fileInput) this.fileInput.nativeElement.value = null;

            if (this.removeAfterUpload)
              setTimeout(() => {
                this.fileUploadService.removeFile(this.fileIndex);
              }, 500);
          });
        }
      } catch (error) {
        this.setTempFile(modifiedFile);
        this.setFileWithError(modifiedFile, JSON.stringify(error));
      }
    } else {
      this.setTempFile(modifiedFile, sizeError);
      this.setFileWithError(modifiedFile, 'file-upload.tooLarge');
    }
  }

  setFileWithError({ name }, error): void {
    const files = (this.fileUploadService.getFiles(this.entityName, this.fileType) || []).map((f) => {
      const fileWithError = f.name === name;
      return fileWithError ? { ...f, error } : f;
    });

    this.fileUploadService.setFiles(files);
  }

  setTempFile(modifiedFile: File, sizeError = false): number {
    const tmpFile: IFile = {
      name: modifiedFile.name,
      size: modifiedFile.size,
      sizeInKB: `${Math.ceil(modifiedFile.size / this.kbSize)}kb`,
      isNew: true,
      fileBinary: modifiedFile,
      fileEntity: fileEntityTypes[this.entityName],
      status: FileUploadStatusEnum.UPLOADING,
      type: this.fileType,
      index: this.fileUploadService.getNextIndex(),
      uploadFlow: this.uploadFlow,
    };
    if (sizeError) {
      this.snackBarService.openSnackBar({
        type: SnackBarsTypes.ERROR,
        description: 'file-upload.maximumFileSize25',
      });
    } else {
      this.fileUploadService.addFile(tmpFile);
    }
    return tmpFile.index;
  }

  sendUploadedFile({ name }, sizeInMB, uri, index): void {
    this.fileUploadService.setFileToUpload(index);
    const files = this.fileUploadService.getFiles(this.entityName).filter((fi) => fi.index !== index);

    if (!uri) return;
    this._imageService.getAuthenticatedUrl(uri).subscribe((authUrls) => {
      const file = {
        ...this.fileUploadService.getFile(index),
        url: uri,
        size: sizeInMB,
        status: FileUploadStatusEnum.FINISHED,
        authUrl: authUrls.authUrl,
        fallbackUrl: authUrls.fallbackUrl,
      };
      this.fileUploadService.setFiles([...files, file]);
      this.fileChanged.emit(file);
      this.urlUpdated.emit(file.url);
    });
  }

  fileName(url: string): string {
    return url.split('/').pop();
  }

  cleanFileName(str: string): string {
    return str?.replace(/[{()}]/g, '');
  }

  initAction(action, index) {
    this[action] && this[action](index);
  }

  allow_rename(index) {
    const files = this.fileUploadService.getFiles(this.entityName).filter((fi) => fi.index !== index);
    const file = this.fileUploadService.getFile(index);
    file.editName = true;
    this.fileUploadService.setFiles([...files, file]);
  }

  setNewName(newName, url, index) {
    const file = this.fileUploadService.getFile(index);

    this.modifiedFile = { ...file, name: newName, uploadFlow: this.uploadFlow };
  }

  saveNewName(index) {
    const files = [
      ...this.fileUploadService.getFiles(this.entityName, FileTypeEnum.DOCUMENTS),
      ...this.fileUploadService.getFiles(this.entityName, FileTypeEnum.IMAGES),
    ].filter((fi) => fi.index !== index);
    const file = this.fileUploadService.getFile(index);
    const extension = file.fileBinary ? file.fileBinary.name.split('.').pop() : file.name.split('.').pop();
    const newFile = {
      ...file,
      name: `${this.cleanFileName(this.modifiedFile?.name)}.${extension}`,
      url: this.modifiedFile.url,
      savingName: true,
    };
    this.fileUploadService.setFiles([...files, newFile]);

    const rewNameFilePatch = {
      operation: {
        op: 'replace',
        path: '/documents',
        value: [...files, newFile],
      } as Operation,
      index,
    };
    this.patchDocumentName.emit(rewNameFilePatch);
  }

  onRemoveFile(fileIndex: number) {
    if (!this.uploadFlow) {
      this.fileUploadService.removeFile(fileIndex);
      this.removeFile.next(fileIndex);
      return;
    }

    this._drawerService.setDisabledButtons(true);
    this.fileUploadService.cancelFileUpload(fileIndex);
  }

  onUploadClick() {
    this.fileInput.nativeElement.click();
  }

  onDownloadErrors() {
    this.downloadErrors.emit();
  }

  get fileIsCanceled(): boolean {
    return this.fileUploadService.uploadIsCanceled(this.fileIndex);
  }

  get fileIsLoading(): boolean {
    return this.fileUploadService.isUploadingFile(this.fileIndex);
  }

  get fileIsProcessing() {
    return this.fileUploadService.isProcessingFile(this.entityName);
  }

  get showUploader() {
    return (
      !this.uploadFlow ||
      (this.uploadFlow && !this.fileList.length) ||
      (this.fileIsCanceled && !(this.hideInputWhileUpload && this.fileList.length) && !this.fileUploadFailed)
    );
  }

  get showFileList() {
    return !this.fileIsCanceled && !this.fileUploadFailed && this.fileList.length > 0;
  }

  public handleImageError(event: any, file: any) {
    const errorUrl = event.target.src;
    if (errorUrl.includes('resizedimages')) {
      file.authUrl = file.fallbackUrl;
    } else {
      file.authUrl = null;
    }
  }
}
